import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import type { Session, User } from '@supabase/supabase-js';
import type { Profile } from '@/types/database';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isCounterStaff: boolean;
  isFinance: boolean;
  isSupport: boolean;
  isHr: boolean;
  isBusStaff: boolean;
  isSupervisor: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  profile: null,
  loading: true,
  isAdmin: false,
  isStaff: false,
  isCounterStaff: false,
  isFinance: false,
  isSupport: false,
  isHr: false,
  isBusStaff: false,
  isSupervisor: false,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const profileFetched = useRef(false);

  // Fetch profile data
  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(data as unknown as Profile);
      }
    } catch {
      // Profile fetch failed — continue without profile
    }
  };

  // Background sync: pull Google/Facebook name+avatar into profile
  const syncOAuthMeta = async (userId: string) => {
    try {
      const currentUser = (await supabase.auth.getUser()).data.user;
      const meta = currentUser?.user_metadata;
      if (!meta) return;

      const { data: existing } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('id', userId)
        .single();
      if (!existing) return;

      const row = existing as unknown as { full_name: string | null; avatar_url: string | null };
      // Named fields, not `Record<string, string>`: supabase-js 2.112 rejects
      // an index-signature type on `.update()`, and rightly — it cannot tell a
      // real column from a typo.
      const updates: { full_name?: string; avatar_url?: string } = {};
      if (!row.full_name && (meta.full_name || meta.name)) updates.full_name = meta.full_name || meta.name;
      if (!row.avatar_url && (meta.avatar_url || meta.picture)) updates.avatar_url = meta.avatar_url || meta.picture;

      if (Object.keys(updates).length > 0) {
        const { data: updated } = await supabase
          .from('profiles')
          .update(updates)
          .eq('id', userId)
          .select('*')
          .single();
        if (updated) setProfile(updated as unknown as Profile);
      }
    } catch {
      // Silent — sync is optional
    }
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    profileFetched.current = false;
  };

  // 1) Auth listener — NO async, just set user/session state
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (!s?.user) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, s) => {
        setSession(s);
        // A SIGNED_IN callback can arrive while the previous unauthenticated
        // render still has `loading === false`. Mark the profile as pending
        // in the same state transition as the new user so post-login
        // redirects cannot fall back to the passenger home before the role
        // has been fetched.
        if (s?.user && !profileFetched.current) {
          setLoading(true);
        }
        setUser(s?.user ?? null);
        if (!s?.user) {
          setProfile(null);
          setLoading(false);
          profileFetched.current = false;
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // 2) When user changes, fetch profile (separate from auth callback to avoid lock issues)
  useEffect(() => {
    if (user?.id) {
      profileFetched.current = false;
      // Hold `loading` true until the profile (and therefore the role) is
      // resolved. Without this, a fresh login on a page whose initial
      // getSession already set loading=false would let role-gated redirects
      // (usePostLoginRedirect) fire before the role is known — sending an
      // admin to "/" instead of "/admin".
      setLoading(true);
      fetchProfile(user.id).then(() => {
        setLoading(false);
        profileFetched.current = true;
        // Fire-and-forget: sync OAuth metadata
        syncOAuthMeta(user.id);
      });
    }
  }, [user?.id]);

  const isAdmin = profile?.role === 'admin';
  // NOTE: isStaff intentionally excludes 'finance' and 'support' roles.
  // Those roles have dedicated portals and separate auth flags (isFinance, isSupport).
  // Do NOT use isStaff as a catch-all for "all non-passenger roles".
  const isStaff = ['driver', 'supervisor', 'staff', 'admin'].includes(profile?.role || '');
  const isCounterStaff = profile?.role === 'counter_staff';
  const isFinance = profile?.role === 'finance';
  const isSupport = profile?.role === 'support';
  const isHr = profile?.role === 'hr';
  const isBusStaff = ['staff', 'supervisor', 'admin'].includes(profile?.role || '');
  const isSupervisor = profile?.role === 'supervisor';

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, isAdmin, isStaff, isCounterStaff, isFinance, isSupport, isHr, isBusStaff, isSupervisor, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
